/**
 * ContactCtaSection — section kind `contact_cta`.
 *
 * Reads `tenant.public_email` and `tenant.phone` directly at render time
 * — same pattern as ContactDetailsSection — so the section is functional
 * with zero dealer config. Self-hides when neither is publishable so a
 * default-seeded contact / about page degrades cleanly on a brand-new
 * tenant.
 *
 * `data` is display-config only (optional headline / subhead / button
 * label overrides / mailto subject). No tenant data flows through
 * `data`, so no token-substitution mechanism is introduced.
 */

import type { Tenant } from '@sifly/shared-types'
import { formatPhone } from '@sifly/shared-utils'

export type ContactCtaSectionData = {
  headline?: string
  subhead?: string
  email_label?: string
  phone_label?: string
  email_subject?: string
}

export type ContactCtaSectionProps = {
  data: ContactCtaSectionData
  tenant: Pick<
    Tenant,
    'name' | 'public_email' | 'phone' | 'show_public_email' | 'show_public_phone'
  >
  /** Optional analytics hook. Fired when the email or phone CTA is clicked.
   * shared-ui stays analytics-agnostic — the storefront passes a callback that
   * routes into its consent-gated funnel `track` path. Must be supplied from a
   * client boundary; never preventDefault. */
  onCtaClick?: (meta: { channel: 'email' | 'phone'; label: string; href: string }) => void
}

export function ContactCtaSection({ data, tenant, onCtaClick }: ContactCtaSectionProps) {
  const showEmail = tenant.show_public_email !== false && !!tenant.public_email
  const showPhone = tenant.show_public_phone !== false && !!tenant.phone

  // Self-hide when there's nothing to call to — better than rendering a
  // headline with no buttons under it.
  if (!showEmail && !showPhone) return null

  const headline = data.headline ?? 'Get in touch'
  const subhead =
    data.subhead ??
    `We respond within one business day — usually faster.`
  const emailLabel = data.email_label ?? 'Email us'
  const phoneLabel = data.phone_label ?? 'Call us'
  const emailHref = data.email_subject
    ? `mailto:${tenant.public_email}?subject=${encodeURIComponent(data.email_subject)}`
    : `mailto:${tenant.public_email}`

  return (
    <section
      className="max-w-[var(--max-width-content)] mx-auto px-4 py-[var(--space-section)] text-center"
      data-component="ContactCtaSection"
    >
      <h2
        className="text-2xl md:text-3xl font-bold text-[var(--color-text-primary)] mb-2"
        style={{ fontFamily: 'var(--font-heading)' }}
      >
        {headline}
      </h2>
      {subhead && (
        <p className="text-[var(--color-text-secondary)] mb-6 max-w-md mx-auto">
          {subhead}
        </p>
      )}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        {showEmail && tenant.public_email && (
          <a
            href={emailHref}
            className="inline-flex items-center justify-center px-5 h-11 rounded-md bg-[var(--color-cta)] text-[var(--color-cta-text,#fff)] text-sm font-medium hover:opacity-90 transition-opacity min-w-[180px]"
            onClick={onCtaClick ? () => onCtaClick({ channel: 'email', label: emailLabel, href: emailHref }) : undefined}
          >
            {emailLabel}
          </a>
        )}
        {showPhone && tenant.phone && (
          <a
            href={`tel:${tenant.phone}`}
            className="inline-flex items-center justify-center px-5 h-11 rounded-md border border-[var(--color-border)] text-[var(--color-text-primary)] text-sm font-medium hover:bg-[var(--color-bg-secondary)] transition-colors min-w-[180px]"
            onClick={onCtaClick ? () => onCtaClick({ channel: 'phone', label: phoneLabel, href: `tel:${tenant.phone}` }) : undefined}
          >
            {phoneLabel}
            <span className="ml-2 text-[var(--color-text-muted)]">{formatPhone(tenant.phone)}</span>
          </a>
        )}
      </div>
    </section>
  )
}
